import React from 'react';
import { Backdrop, Box, Modal, CircularProgress } from '@mui/material';
import { spinAnimation } from '../utils/animations';

export interface CustomLoadingProps {
  loading: boolean;
  background?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'spinner' | 'circular' | 'dots';
  fullScreen?: boolean;
  text?: string;
  color?: string;
  backdropColor?: string;
  zIndex?: number;
}

const CustomLoading: React.FC<CustomLoadingProps> = ({ 
  loading,
  background = 'transparent',
  size = 'medium',
  variant = 'spinner',
  fullScreen = true,
  text,
  color = '#0B9DBD',
  backdropColor = 'rgba(0, 0, 0, 0.5)',
  zIndex = 1300,
}) => {
  const getSize = () => {
    switch (size) {
      case 'small':
        return { width: '30px', height: '30px', borderWidth: '3px' };
      case 'large':
        return { width: '70px', height: '70px', borderWidth: '5px' };
      case 'medium':
      default:
        return { width: '50px', height: '50px', borderWidth: '4px' };
    }
  };

  const sizeConfig = getSize();

  const renderLoader = () => {
    switch (variant) {
      case 'circular':
        return (
          <CircularProgress 
            size={parseInt(sizeConfig.width)} 
            sx={{ color }} 
          />
        );
      
      case 'dots':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  animation: `pulse 1.4s infinite ease-in-out both`,
                  animationDelay: `${index * 0.16}s`,
                  '@keyframes pulse': {
                    '0%, 80%, 100%': {
                      transform: 'scale(0)',
                      opacity: 0.5,
                    },
                    '40%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
        );
      
      case 'spinner':
      default:
        return (
          <Box
            sx={{
              width: sizeConfig.width,
              height: sizeConfig.height,
              border: `${sizeConfig.borderWidth} solid #f3f3f3`,
              borderTop: `${sizeConfig.borderWidth} solid ${color}`,
              borderRadius: '50%',
              animation: `${spinAnimation} 1s linear infinite`,
            }}
          />
        );
    }
  };

  if (!fullScreen) {
    return loading ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          padding: 2,
          background,
        }}
      >
        {renderLoader()}
        {text && (
          <Box
            sx={{
              color: color,
              fontSize: '14px',
              fontWeight: 500,
              mt: 1,
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    ) : null;
  }

  return (
    <Modal
      open={loading}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ 
        timeout: 500,
        sx: { backgroundColor: backdropColor }
      }}
      sx={{ 
        background,
        zIndex,
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}
      >
        {renderLoader()}
        {text && (
          <Box
            sx={{
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 500,
              mt: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CustomLoading;